import { useState, useEffect } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarBlank, CheckCircle, WhatsappLogo } from "@phosphor-icons/react";
import { api, formatApiErrorDetail, SERVICES, CONTACT } from "@/lib/api";

const empty = { name: "", phone: "", address: "", service: "", message: "" };

export default function BookingDialog({ open, onOpenChange, defaultService }) {
  const [form, setForm] = useState(empty);
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // When the dialog opens with a pre-selected service, apply it.
  useEffect(() => {
    if (open && defaultService) {
      setForm((f) => ({ ...f, service: defaultService }));
    }
  }, [open, defaultService]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const buildWhatsAppUrl = (data, chosenDate) => {
    const text =
`*New Booking — Quickmaid Services*

*Name:* ${data.name}
*Phone:* ${data.phone}
*Service:* ${data.service}
*Preferred Date:* ${chosenDate ? format(chosenDate, "PPP") : "-"}
*Address:* ${data.address}
*Notes:* ${data.message || "—"}`;
    return `https://wa.me/${CONTACT.phoneIntl}?text=${encodeURIComponent(text)}`;
  };

  const [whatsappUrl, setWhatsappUrl] = useState("");

  const reset = () => {
    setForm(empty);
    setDate(null);
    setDone(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address || !form.service || !date) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        ...form,
        service: form.service,
        preferred_date: format(date, "yyyy-MM-dd"),
      });
      const url = buildWhatsAppUrl(form, date);
      setWhatsappUrl(url);
      setDone(true);
      toast.success("Booking request sent! We'll call you shortly.");
      // Auto-open WhatsApp with prefilled details so the customer can also
      // ping us on WhatsApp in one tap.
      window.open(url, "_blank", "noopener");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) setTimeout(reset, 200);
      }}
    >
      <DialogContent
        className="sm:max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="booking-dialog"
      >
        {done ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="py-8 text-center"
          >
            <div className="mx-auto grid place-items-center h-16 w-16 rounded-full bg-[var(--gold)]/20 text-[var(--gold)] mb-5">
              <CheckCircle weight="fill" size={40} />
            </div>
            <h3 className="font-display text-2xl text-[var(--navy)]">
              Request Received!
            </h3>
            <p className="text-slate-600 mt-2">
              Thank you, {form.name.split(" ")[0]}. Our team will contact you on{" "}
              {form.phone} shortly.
            </p>
            <p className="text-slate-500 text-sm mt-4">
              We've also opened WhatsApp — just tap <b>Send</b> to notify us instantly.
            </p>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1eb959] text-white font-bold px-6 py-3 transition-colors"
              data-testid="booking-done-whatsapp"
            >
              <WhatsappLogo size={22} weight="fill" /> Send on WhatsApp
            </a>
            <div className="mt-5">
              <Button
                variant="ghost"
                className="text-slate-500"
                onClick={() => onOpenChange(false)}
                data-testid="booking-done-close"
              >
                Close
              </Button>
            </div>
          </motion.div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl text-[var(--navy)]">
                Book a Service
              </DialogTitle>
              <DialogDescription>
                Fill in your details and we&apos;ll get back to you quickly.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-4 mt-2">
              <div className="space-y-1.5">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your name"
                  data-testid="booking-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="10-digit mobile number"
                  data-testid="booking-phone"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Service Needed *</Label>
                <Select
                  value={form.service}
                  onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}
                >
                  <SelectTrigger data-testid="booking-service">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`service-opt-${s.split(" ")[0].toLowerCase()}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Preferred Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-start font-normal"
                      data-testid="booking-date-trigger"
                    >
                      <CalendarBlank size={18} className="mr-2 text-[var(--navy)]" />
                      {date ? format(date, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="address">Address *</Label>
                <Textarea
                  id="address"
                  value={form.address}
                  onChange={set("address")}
                  placeholder="Your full address in Pune"
                  rows={2}
                  data-testid="booking-address"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="message">Additional Notes</Label>
                <Textarea
                  id="message"
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Anything we should know? (optional)"
                  rows={2}
                  data-testid="booking-message"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full rounded-full bg-[var(--navy)] hover:bg-[var(--navy-deep)] font-bold py-6 text-base"
                data-testid="booking-submit"
              >
                {submitting ? "Sending..." : "Submit Request"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
